/**
 * THE SHOPPER'S LANGUAGE, IN ONE PLACE (2026-10-26, Swe Sosetta run 3, panel S1).
 *
 * A Hebrew shopper on an Arabic bakery's page read «מקרוטה» in the title and «בחרו نكهة» on the button under it,
 * Arabic flavours in a Hebrew sentence on WhatsApp, and the Arabic name on the grid, in the cart and on the related
 * strip. Every surface had answered "which language is this?" for itself: the title read `translations`, the button
 * read the choice's `.name` while the products carry `{ label }`, the values were looked up only in the shop-wide
 * choice library (a product's own choices are not in it), and the grid, the cart and the strips printed the shop's
 * own words to everyone.
 *
 * THE RULE, ONCE: the row's OWN translation in the page's language → the shop's shared choice library → the words
 * the shop wrote. Name, description, category, choice name (`name ?? label`) and option values all go through here,
 * and the surfaces take a product already put into the reader's language (`localizedProduct`) so a new read of
 * `product.name` cannot quietly be the shop's language again.
 *
 * The Node server's link previews read the same rules from `server-shopper-text.core.js` (CRA will not import from
 * outside `src/`, and the server image copies only `server*.js`); `theShopperTextIsTheSameOnBothSides.test.js` holds
 * the two to the same answers.
 *
 * VALUES ARE NEVER REWRITTEN on the product: they are what a variant is matched by and what the cart and the
 * checkout send. Only what is DRAWN goes through `optionValueIn`.
 */

export const SLOTS = ['color', 'size', 'style'];

export function langOf(language) {
  return String(language || '').toLowerCase().split(/[-_]/)[0];
}

/** `translations[lang][field]`, trimmed — a value's translation may also be stored as a bare string. */
function ownText(translations, lang, field) {
  if (!translations || typeof translations !== 'object') return '';
  const tr = translations[lang];
  if (typeof tr === 'string') return field === 'name' ? tr.trim() : '';
  if (!tr || typeof tr !== 'object') return '';
  const v = tr[field];
  return typeof v === 'string' ? v.trim() : '';
}

function baseText(entity, field) {
  const v = entity && typeof entity === 'object' ? entity[field] : null;
  return typeof v === 'string' ? v.trim() : '';
}

/** An entity's text in the reader's language: its own translation, else the shop's own words. */
export function textIn(entity, language, field = 'name') {
  if (!entity || typeof entity !== 'object') return '';
  const f = field || 'name';
  return ownText(entity.translations, langOf(language), f) || baseText(entity, f);
}

export const productNameIn = (product, language) => textIn(product, language, 'name');
export const categoryNameIn = (category, language) => textIn(category, language, 'name');

/**
 * The description in the reader's language. The structured `descriptionHtml` is the shop's own language, so it is
 * kept only when the plain text is the shop's own too — a translated description is shown as the translation, never
 * beside (or under) the original's markup.
 */
export function productDescriptionIn(product, language) {
  if (!product || typeof product !== 'object') return { text: '', html: null };
  const translated = ownText(product.translations, langOf(language), 'description');
  const base = baseText(product, 'description');
  if (translated && translated !== base) return { text: translated, html: null };
  return { text: translated || base, html: product.descriptionHtml ?? null };
}

/** A choice's name ("Flavour" / "טעם" / "نكهة"). Choices are written `{ label }` by some writers and `{ name }` by
 *  others, so both are read — `name` first. */
export function choiceNameIn(choice, language) {
  if (!choice || typeof choice !== 'object') return '';
  const lang = langOf(language);
  return ownText(choice.translations, lang, 'name')
    || ownText(choice.translations, lang, 'label')
    || baseText(choice, 'label')
    || baseText(choice, 'name');
}

/** The choice behind a picker slot (`color` / `size` / `style` = the first three choices by position). */
export function slotChoice(product, slot) {
  const i = SLOTS.indexOf(slot);
  if (i < 0 || !product || typeof product !== 'object') return null;
  if (Array.isArray(product.choices) && product.choices.length) {
    const ordered = product.choices.slice().sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));
    if (ordered[i]) return ordered[i];
  }
  if (product.axisChoices && typeof product.axisChoices === 'object' && product.axisChoices[slot]) return product.axisChoices[slot];
  if (i === 0 && product.mainChoice && typeof product.mainChoice === 'object') return product.mainChoice;
  return null;
}

/** A slot's heading: its choice's name in the reader's language, else the label the shop gave it, else "". */
export function axisNameIn(product, slot, language) {
  const choice = slotChoice(product, slot);
  const own = choice ? choiceNameIn(choice, language) : '';
  if (own) return own;
  const labels = product && product.axisLabels;
  return labels && typeof labels[slot] === 'string' ? labels[slot].trim() : '';
}

/** The choice key behind a slot — `flavor`, `size`, `color` … — or the slot's own name for a payload that names none. */
export function slotKey(product, slot) {
  const choice = slotChoice(product, slot);
  if (choice && typeof choice.key === 'string' && choice.key) return choice.key;
  if (product?.axisKeys && typeof product.axisKeys[slot] === 'string' && product.axisKeys[slot]) return product.axisKeys[slot];
  return slot;
}

/** The slot a choice key sits in on this product, or null. */
export function slotOfKey(product, key) {
  return SLOTS.find((slot) => slotKey(product, slot) === key && (slotChoice(product, slot) || slot === key)) ?? null;
}

function fromValueMap(map, value, lang) {
  if (!map || typeof map !== 'object') return '';
  return ownText(map[value], lang, 'name');
}

/**
 * An option value as the reader reads it: the product's OWN word for it (its main card's translations, its choice's
 * own `values`), then the shop's choice library as the server merged it (`axisValueTranslations` / the choice's
 * `valueTranslations`), then the value exactly as the shop wrote it. "" for no value.
 */
export function optionValueIn(product, slot, value, language) {
  if (value == null || value === '') return '';
  const raw = String(value);
  const lang = langOf(language);
  const choice = slotChoice(product, slot);
  if (SLOTS.indexOf(slot) === 0 && Array.isArray(product?.mainVariants)) {
    const main = product.mainVariants.find((m) => m && m.value === raw);
    const own = main ? ownText(main.translations, lang, 'name') : '';
    if (own) return own;
  }
  const choiceTr = choice?.translations?.[lang];
  const ownValues = choiceTr && typeof choiceTr === 'object' && choiceTr.values && typeof choiceTr.values === 'object' ? choiceTr.values : null;
  const word = ownValues ? ownValues[raw] : null;
  const choiceOwn = typeof word === 'string' ? word.trim() : (word && typeof word.name === 'string' ? word.name.trim() : '');
  if (choiceOwn) return choiceOwn;
  const merged = fromValueMap(product?.axisValueTranslations?.[slot], raw, lang)
    || fromValueMap(choice?.valueTranslations, raw, lang);
  if (merged) return merged;
  return raw.trim();
}

/** One option's words — "טעם נוטלה, משקל 500 ג׳" — from its slot values (`{ color, size, style }`), each slot named and
 *  valued in the reader's language; the agent's card builds its shop-language `label` the same way. "" for none. */
export function optionLabelIn(product, values, language) {
  if (!values || typeof values !== 'object') return '';
  return SLOTS
    .filter((slot) => values[slot] != null && values[slot] !== '')
    .map((slot) => {
      const name = axisNameIn(product, slot, language);
      const value = optionValueIn(product, slot, values[slot], language);
      return name ? `${name} ${value}` : value;
    })
    .join(', ');
}

/** Preset colour names an owner picks in the portal ("Black") — the storefront's own words for them. */
const COLOR_I18N_KEYS = {
  black: 'productDetail.colors.black',
  white: 'productDetail.colors.white',
  gray: 'productDetail.colors.gray',
  grey: 'productDetail.colors.gray',
  beige: 'productDetail.colors.beige',
  red: 'productDetail.colors.red',
  pink: 'productDetail.colors.pink',
  orange: 'productDetail.colors.orange',
  yellow: 'productDetail.colors.yellow',
  green: 'productDetail.colors.green',
  blue: 'productDetail.colors.blue',
  purple: 'productDetail.colors.purple',
  brown: 'productDetail.colors.brown',
};

export function presetColourName(value, t) {
  if (!value) return '';
  const key = COLOR_I18N_KEYS[String(value).trim().toLowerCase()];
  if (key && typeof t === 'function') return t(key, { defaultValue: value });
  return String(value);
}

/** True when a slot's choice holds colours (so a preset colour may wear the storefront's own word). A payload with no
 *  choice metadata keeps the old reading: the first slot is a colour. */
function slotIsColour(product, slot) {
  if (slot !== 'color') return false;
  const vt = slotChoice(product, slot)?.valueType;
  return vt ? vt === 'COLOR' : true;
}

/** `optionValueIn`, then — only when nothing above it answered — a preset colour in the storefront's own words. */
export function optionValueShown(product, slot, value, language, t) {
  const shown = optionValueIn(product, slot, value, language);
  if (shown !== String(value ?? '').trim()) return shown;
  return slotIsColour(product, slot) ? presetColourName(shown, t) : shown;
}

/* ── AN OPEN PICK ASKS FOR A NOTE (Swe Sosetta run 4, round B: S7) ─────────────────────────────────────────────────
 * The tart's «نكهة وحدة» leaves the flavour open — the buyer writes it. The builder marks such a value (W6):
 * `valueMeta.notePrompt = { ar, he, en }` — on its main card (`mainVariants[].valueMeta`, a card's `swatches[]`), on its
 * choice's value (`choices[].values[].meta`) or on an option row (`subVariants[].options[].valueMeta`).
 *
 * THE MARKER TRAVELS ON EVERY PAYLOAD THE STOREFRONT ADDS FROM (fix round 1): a card — the grid, the strips, the
 * related strip, the cart's suggestions, the checkout's shelf rows — carries no option rows, so the serializer sends
 * each slot's value metadata as `axisValueMeta[slot][value]` (Stream W, `axisChoicesOf`); read here like the rest. */
function hasPrompt(meta) {
  const p = meta && typeof meta === 'object' ? meta.notePrompt : null;
  return p && (typeof p === 'string' ? p.trim() : typeof p === 'object') ? p : null;
}

/** The note prompt a value carries (`{ ar, he, en }` or a string), wherever the payload carries it; null for none. */
export function notePromptMeta(product, slot, value) {
  if (!product || typeof product !== 'object' || value == null || value === '') return null;
  const raw = String(value);
  const carded = product.axisValueMeta && typeof product.axisValueMeta === 'object' ? product.axisValueMeta[slot] : null;
  if (carded && typeof carded === 'object' && hasPrompt(carded[raw])) return hasPrompt(carded[raw]);
  if (SLOTS.indexOf(slot) === 0) {
    for (const m of Array.isArray(product.mainVariants) ? product.mainVariants : []) {
      if (m && m.value === raw && hasPrompt(m.valueMeta)) return hasPrompt(m.valueMeta);
    }
    for (const w of Array.isArray(product.swatches) ? product.swatches : []) {
      if (w && w.value === raw && hasPrompt(w.valueMeta)) return hasPrompt(w.valueMeta);
    }
  }
  const choice = slotChoice(product, slot);
  for (const v of Array.isArray(choice?.values) ? choice.values : []) {
    if (v && v.value === raw && hasPrompt(v.meta)) return hasPrompt(v.meta);
  }
  const key = slotKey(product, slot);
  for (const m of Array.isArray(product.mainVariants) ? product.mainVariants : []) {
    for (const sub of Array.isArray(m?.subVariants) ? m.subVariants : []) {
      for (const o of Array.isArray(sub?.options) ? sub.options : []) {
        if (o && o.key === key && o.value === raw && hasPrompt(o.valueMeta)) return hasPrompt(o.valueMeta);
      }
    }
  }
  return null;
}

/** A note prompt in the reader's language — its own, else English, else any it has; "" for none. */
export function promptIn(prompt, language) {
  if (!prompt) return '';
  if (typeof prompt === 'string') return prompt.trim();
  if (typeof prompt !== 'object') return '';
  const pick = prompt[langOf(language)] ?? prompt.en ?? Object.values(prompt).find((v) => typeof v === 'string' && v.trim());
  return typeof pick === 'string' ? pick.trim() : '';
}

/** The line a chosen open pick shows under its choice, in the reader's language; "" for a value that asks nothing. */
export const notePromptOf = (product, slot, value, language) => promptIn(notePromptMeta(product, slot, value), language);

/** The choices a variant row was bought with, as `{ key, label, value, shown }` — `value` exactly as stored (what the
 *  shop matches an order by), `label` and `shown` in the reader's language. An open pick also carries its
 *  `notePrompt` (every language — the cart outlives a language switch), so the cart, the checkout's note and both
 *  WhatsApp messages know a value is still to be written (S7). */
export function variantOptionsIn(product, variant, language, t) {
  if (!variant) return [];
  return SLOTS.map((slot) => {
    const value = variant[slot];
    if (value == null || value === '') return null;
    const notePrompt = notePromptMeta(product, slot, value);
    return {
      key: slot,
      label: axisNameIn(product, slot, language) || null,
      value,
      shown: optionValueShown(product, slot, value, language, t),
      ...(notePrompt ? { notePrompt } : {}),
    };
  }).filter(Boolean);
}

const ARABIC_SCRIPT = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * 'arabic' for a text in Arabic script, else undefined — for `data-script` on a clamped name (S10): the storefront's
 * Arabic face (Cairo) hangs the dots of a final ي below a 1.25 line box, and a box that clips its overflow (every
 * two-line card name) cut them off — «مني» printed «منى». The stylesheets give such a name the room its dots need.
 */
export const scriptOf = (text) => (ARABIC_SCRIPT.test(String(text ?? '')) ? 'arabic' : undefined);

/** FSI … PDI: a value inside a sentence keeps its own direction («500 g» never reads «g 500» in an Arabic line). */
export const isolate = (text) => (text == null || text === '' ? '' : `⁨${text}⁩`);

const LOCALIZED = '__shopperLanguage';
const cache = new WeakMap();

/**
 * THE PRODUCT, IN THE READER'S LANGUAGE — what every surface draws from. Name, description (and its markup only when
 * it is the shop's own text), category, the choices' names and the slot labels are replaced by the reader's words;
 * values, ids, prices and photos are untouched. `addressName` keeps the shop's own name, which is what the product's
 * address is minted from (utils/productUrl): a Hebrew reader and an Arabic one share one link.
 *
 * Memoised per product object and language, so a grid of 500 cards costs one walk per card.
 */
export function localizedProduct(product, language) {
  if (!product || typeof product !== 'object') return product;
  const lang = langOf(language);
  if (product[LOCALIZED] === lang) return product;
  let byLang = cache.get(product);
  if (byLang && byLang.has(lang)) return byLang.get(lang);
  const description = productDescriptionIn(product, lang);
  const axisLabels = { ...(product.axisLabels || {}) };
  for (const slot of SLOTS) {
    const name = axisNameIn(product, slot, lang);
    if (name) axisLabels[slot] = name;
  }
  const out = {
    ...product,
    addressName: product.addressName ?? product.name,
    name: productNameIn(product, lang) || product.name,
    description: description.text || product.description || null,
    descriptionHtml: description.html,
    category: product.category && typeof product.category === 'object'
      ? { ...product.category, name: categoryNameIn(product.category, lang) || product.category.name }
      : product.category,
    choices: Array.isArray(product.choices)
      ? product.choices.map((c) => (c && typeof c === 'object' ? { ...c, label: choiceNameIn(c, lang) || c.label } : c))
      : product.choices,
    mainChoice: product.mainChoice && typeof product.mainChoice === 'object'
      ? { ...product.mainChoice, label: choiceNameIn(product.mainChoice, lang) || product.mainChoice.label }
      : product.mainChoice,
    axisLabels,
  };
  Object.defineProperty(out, LOCALIZED, { value: lang, enumerable: false });
  if (!byLang) { byLang = new Map(); cache.set(product, byLang); }
  byLang.set(lang, out);
  return out;
}
